import { useState } from "react";
import { useTranslation } from "react-i18next";
import { ExclamationTriangleIcon } from "@heroicons/react/24/outline";

import { ICallSheet } from "../../../../../models/CallQueue";
import classNames from "../../../../../utils/classNames";
import { barPosition } from "../../../../../utils/callQueue";
import PriceFormat from "../../../../../utils/priceFormat";
import { Callout, SectionCard } from "../Pill";

function Tile({ label, value, help, emphasis = false, tone }: { label: string; value: string; help: string; emphasis?: boolean; tone?: "red" }) {
  return (
    <div
      className={classNames(
        "flex flex-col gap-0.5 rounded-lg px-3.5 py-3",
        emphasis ? "border-2 border-green-600 bg-green-50" : "border border-gray-200",
        tone === "red" && "border-red-200 bg-red-50"
      )}
    >
      <span className={classNames("text-xs", emphasis ? "font-semibold text-green-800" : "text-gray-600")}>{label}</span>
      <span
        className={classNames(
          "text-[28px] tabular-nums",
          emphasis ? "font-bold text-green-800" : "font-semibold text-gray-900",
          tone === "red" && "text-red-700"
        )}
      >
        {value}
      </span>
      <span className={classNames("text-xs", emphasis ? "text-green-800" : "text-gray-600")}>{help}</span>
    </div>
  );
}

export default function OfferPanel({
  sheet,
  agreedPrice,
  setAgreedPrice,
  onBook,
  onAskManager,
  isSaving,
}: {
  sheet: ICallSheet;
  agreedPrice: string;
  setAgreedPrice: (value: string) => void;
  onBook: () => void;
  onAskManager: (price: number, note: string) => void;
  isSaving: boolean;
}) {
  const { t } = useTranslation("leads");
  const [asking, setAsking] = useState(false);
  const [askNote, setAskNote] = useState("");

  const max = sheet.approvedPrice ?? sheet.offer.max;
  const floor = sheet.offer.floor ?? null;
  const overMax = sheet.headroom < 0;
  const agreed = Number(agreedPrice);
  const agreedValid = agreedPrice.trim() !== "" && Number.isFinite(agreed) && agreed > 0;
  const aboveLimit = agreedValid && agreed > sheet.bookingLimit;
  const validUntil = new Date(sheet.validUntil).toLocaleDateString("en-GB", { day: "numeric", month: "short" });
  const barFloor = floor ?? Math.min(sheet.quotedPrice, max);
  // Clamped to the bar: an over-max quote sits on the max marker and the room fill disappears.
  const quotedAt = barPosition(sheet.quotedPrice, barFloor, max);

  return (
    <SectionCard title={t("Your offer")} right={`${t("Valid until")} ${validUntil}`}>
      <div className="flex flex-col gap-4 px-4 py-4 sm:px-5">
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
          <Tile
            label={sheet.priceChanged ? t("Last offered") : t("Open at (quoted)")}
            value={PriceFormat(sheet.quotedPrice)}
            help={sheet.priceChanged ? `${t("Online quote")} ${PriceFormat(sheet.onlineQuote)}` : t("What the customer saw online")}
          />
          <Tile
            label={sheet.approvedPrice ? t("Approved up to") : t("Max you can offer")}
            value={PriceFormat(max)}
            help={t("Walk away above this")}
            emphasis
          />
          <Tile
            label={overMax ? t("Over max") : t("Room to negotiate")}
            value={`${overMax ? "−" : "+"}${PriceFormat(Math.abs(sheet.headroom))}`}
            help={overMax ? t("The quote is above what you can pay") : t("Use in steps of {{step}}", { step: PriceFormat(sheet.negotiationStep) })}
            tone={overMax ? "red" : undefined}
          />
        </div>

        {overMax && (
          <Callout tone="red" icon={<ExclamationTriangleIcon className="h-4 w-4" aria-hidden="true" />}>
            <strong>{t("Over max.")}</strong>{" "}
            {t("The customer was quoted {{quoted}}, but you can only book up to {{limit}}. Above that needs a manager's approval.", {
              quoted: PriceFormat(sheet.quotedPrice),
              limit: PriceFormat(sheet.bookingLimit),
            })}
          </Callout>
        )}

        <div className="flex flex-col gap-2">
          <div className="relative h-3.5 rounded-full bg-slate-200" aria-hidden="true">
            {!overMax && <div className="absolute inset-y-0 right-0 rounded-r-full bg-green-300" style={{ left: `${quotedAt}%` }} />}
            <div className={classNames("absolute -top-1 h-[22px] w-[3px] rounded", overMax ? "bg-red-600" : "bg-gray-900")} style={{ left: `calc(${quotedAt}% - 1.5px)` }} />
            <div className="absolute -top-1 right-0 h-[22px] w-[3px] rounded bg-green-800" />
          </div>
          <div className="relative h-9 text-xs">
            <span className="absolute left-0 top-0 flex flex-col text-gray-600">
              <span className="font-semibold text-slate-700">{floor !== null ? PriceFormat(floor) : "—"}</span>
              {t("Floor")}
            </span>
            <span className="absolute top-0 flex -translate-x-1/2 flex-col items-center text-gray-600" style={{ left: `${Math.min(85, Math.max(15, quotedAt))}%` }}>
              <span className="font-semibold text-gray-900">{PriceFormat(sheet.quotedPrice)}</span>
              {t("Quoted")}
            </span>
            <span className="absolute right-0 top-0 flex flex-col items-end text-green-800">
              <span className="font-semibold">{PriceFormat(max)}</span>
              {t("Max")}
            </span>
          </div>
        </div>

        <div className="flex flex-wrap items-end gap-3 border-t border-gray-100 pt-3">
          <label className="flex flex-col gap-1 text-xs font-medium text-gray-700">
            {t("Agreed price")}
            <span className="flex min-h-[44px] w-40 items-center rounded-md border border-gray-300 bg-white px-2.5 focus-within:ring-2 focus-within:ring-primary-600">
              <span className="text-base text-gray-500">£</span>
              <input
                type="text"
                inputMode="decimal"
                value={agreedPrice}
                onChange={(e) => setAgreedPrice(e.target.value.replace(/[^\d.]/g, ""))}
                aria-label={t("Agreed price in pounds")}
                className="w-full border-0 bg-transparent py-0 pl-1 text-base font-semibold text-gray-900 focus:ring-0"
              />
            </span>
          </label>
          <button
            type="button"
            onClick={onBook}
            disabled={!agreedValid || aboveLimit || isSaving}
            className="min-h-[44px] rounded-md border border-primary-600 bg-primary-600 px-4 py-2.5 text-sm font-semibold text-white shadow-sm hover:bg-primary-700 disabled:cursor-not-allowed disabled:opacity-50"
          >
            {t("Book collection at {{price}}", { price: agreedValid ? PriceFormat(agreed) : "—" })}
          </button>
          <button
            type="button"
            onClick={() => setAsking((x) => !x)}
            aria-expanded={asking}
            className="min-h-[44px] rounded-md border border-gray-300 bg-white px-3.5 py-2.5 text-[13px] font-medium text-gray-700 hover:bg-gray-50"
          >
            {t("Ask manager for more")}
          </button>
          <span className={classNames("flex-1 basis-48 text-xs", aboveLimit ? "font-medium text-red-700" : "text-gray-600")}>
            {t("Anything above {{limit}} goes to a manager for approval.", { limit: PriceFormat(sheet.bookingLimit) })}
          </span>
        </div>

        {sheet.pendingApproval && (
          <Callout tone="amber">
            {t("Waiting for a manager to approve {{price}}.", { price: PriceFormat(sheet.pendingApproval.requestedPrice) })}
          </Callout>
        )}

        {asking && (
          <div className="flex flex-wrap items-end gap-3 rounded-lg border border-amber-200 bg-amber-50 p-3">
            <label className="flex flex-1 basis-60 flex-col gap-1 text-xs font-medium text-amber-900">
              {t("Why do you need more?")}
              <input
                type="text"
                value={askNote}
                onChange={(e) => setAskNote(e.target.value)}
                placeholder={t("e.g. Has a written offer of £200")}
                className="min-h-[44px] rounded-md border border-amber-200 text-sm text-gray-900"
              />
            </label>
            <button
              type="button"
              disabled={!agreedValid || !aboveLimit || isSaving}
              onClick={() => {
                onAskManager(agreed, askNote);
                setAsking(false);
                setAskNote("");
              }}
              className="min-h-[44px] rounded-md border border-amber-700 bg-amber-700 px-3.5 py-2 text-[13px] font-semibold text-white disabled:opacity-50"
            >
              {t("Ask for {{price}}", { price: agreedValid ? PriceFormat(agreed) : "—" })}
            </button>
            <span className="basis-full text-xs text-amber-900">
              {t("Put the price you want to offer in Agreed price first. Then save Call back so the customer knows you'll ring them.")}
            </span>
          </div>
        )}
      </div>
    </SectionCard>
  );
}
